/**
 * Network transport for ESC/POS thermal printers.
 *
 * Browsers cannot make raw TCP connections. This transport supports two modes:
 * 1. HTTP POST — many modern network printers expose an HTTP endpoint for printing
 * 2. WebSocket — if a local relay agent is running (ws://localhost:{port})
 *
 * Limitation: raw TCP (port 9100) is NOT possible from a browser.
 * For printers that only support raw TCP, a small local relay agent is needed.
 */
import type { PrinterTransport, PrinterDeviceInfo } from '../types';

export class NetworkTransport implements PrinterTransport {
  readonly type = 'network' as const;
  private ip: string;
  private port: number;
  private connected = false;
  private ws: WebSocket | null = null;

  constructor(ip: string, port = 9100) {
    this.ip = ip;
    this.port = port;
  }

  /** Update the connection target. */
  configure(ip: string, port: number): void {
    this.ip = ip;
    this.port = port;
  }

  async connect(): Promise<void> {
    if (!this.ip) {
      throw new Error('Printer IP address not configured');
    }

    // Try WebSocket first (local relay agent)
    try {
      await this.connectWebSocket();
      this.connected = true;
      return;
    } catch {
      // WebSocket failed, try HTTP probe
    }

    // Probe HTTP endpoint
    try {
      const res = await fetch(`http://${this.ip}:${this.port}/`, {
        method: 'HEAD',
        signal: AbortSignal.timeout(3000),
      });
      if (res.ok || res.status === 405) {
        // Printer responds to HTTP — we can POST print data
        this.connected = true;
        return;
      }
    } catch {
      // HTTP probe also failed
    }

    throw new Error(
      `Cannot reach printer at ${this.ip}:${this.port}. ` +
      'Ensure the printer is on and a local print relay is running for raw TCP printers.',
    );
  }

  async disconnect(): Promise<void> {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.connected = false;
  }

  async write(data: Uint8Array): Promise<void> {
    if (!this.connected) {
      throw new Error('Printer not connected');
    }

    // Prefer WebSocket if available
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data);
      return;
    }

    // Fall back to HTTP POST
    try {
      const res = await fetch(`http://${this.ip}:${this.port}/print`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: data as unknown as BodyInit,
        signal: AbortSignal.timeout(10_000),
      });
      if (!res.ok) {
        throw new Error(`Print request failed (HTTP ${res.status})`);
      }
    } catch (err) {
      this.connected = false;
      throw err instanceof Error ? err : new Error('Network print failed');
    }
  }

  isConnected(): boolean {
    return this.connected;
  }

  getDeviceInfo(): PrinterDeviceInfo | null {
    if (!this.ip) return null;
    return {
      name: `Network Printer (${this.ip})`,
      type: 'network',
      ipAddress: this.ip,
      port: this.port,
    };
  }

  private connectWebSocket(): Promise<void> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('WebSocket connection timeout'));
      }, 3000);

      try {
        this.ws = new WebSocket(`ws://${this.ip}:${this.port}`);
        this.ws.binaryType = 'arraybuffer';

        this.ws.onopen = () => {
          clearTimeout(timeout);
          resolve();
        };

        this.ws.onerror = () => {
          clearTimeout(timeout);
          this.ws = null;
          reject(new Error('WebSocket connection failed'));
        };

        this.ws.onclose = () => {
          this.ws = null;
          this.connected = false;
        };
      } catch {
        clearTimeout(timeout);
        reject(new Error('WebSocket not available'));
      }
    });
  }
}
